import React, { useRef } from "react";
import { useAtomValue } from "jotai";
import { Card, Skeleton, Tooltip, Typography, useMediaQuery } from "@mui/material";

import { nFormatter, classNames, percentageToMultiplier } from "@/lib/utils";
import { positiveState, negativeState, newbackgroundColorState, themeState } from "@/lib/store";
import StockShortsDailyInterestChart from "../stock-shorts-daily-interest-chart/StockShortsDailyInterestChart";
import styles from "./StockShortsCards.module.scss";

type StockShortsCardsProps = {
    activeTimeRange: string;
    data: any[];
    keys: any[];
    loading: boolean;
    isMounted: React.MutableRefObject<boolean>;
    obfuscated: boolean;
    shortInterest: ShortInterestTypes;
    timeRanges: any[];
};

type ShortInterestTypes = {
    [key: string]: {
        key: string;
        measure: number;
        metric: string;
    };
};

// Availability is stored as a percent and capped at 9999% (99.99x the shares on loan).
const AVAILABILITY_CAP = 9999;
const AVAILABILITY_INFO = "Shares still available to borrow, as a multiple of the shares already on loan. Lower means a tighter lending market: below 1x, fewer shares are left to borrow than are already borrowed.";
const AVAILABILITY_UNLIMITED_INFO = "More than 100x the shares currently on loan are available to borrow.";
const SKELETON_CARD_COUNT = 5;

const isAvailabilityCapped = (value: unknown) => typeof value === "number" && value >= AVAILABILITY_CAP;

const formatAvailability = (value: unknown) =>
    isAvailabilityCapped(value) ? (
        <>
            Unlimited
            <Tooltip arrow title={AVAILABILITY_UNLIMITED_INFO}>
                <sup style={{ cursor: "help", fontSize: "0.7em", marginLeft: 1 }}>*</sup>
            </Tooltip>
        </>
    ) : (
        percentageToMultiplier(value as number)
    );

const StockShortsCards = ({ data, keys, activeTimeRange = "3m", loading, obfuscated, timeRanges, shortInterest }: StockShortsCardsProps) => {
    const negative = useAtomValue(negativeState);
    const newbackgroundColor = useAtomValue(newbackgroundColorState);
    const positive = useAtomValue(positiveState);
    const theme = useAtomValue(themeState);

    const isPositive = (value: number) => value > 0;
    const is2KorGreater = useMediaQuery("(min-width: 2560px)");
    const isLaptop = useMediaQuery("(max-width:1280px)");

    const currentValues = useRef({});

    const obfuscatedClassName = obfuscated ? "obfuscated" : null;

    const boxes = Object.entries(shortInterest)
        .filter(([key]) => !["Date"].includes(key) && key !== "metadata")
        .sort((a: any, b: any) => a[1].order - b[1].order);

    const cardSx = {
        background: newbackgroundColor,
        borderColor: theme === "dark" ? `rgb(62, 62, 62)` : "none",
        borderRight: isLaptop ? "none" : theme === "dark" ? "1px solid rgb(62, 62, 62)" : "none",
        borderLeft: isLaptop ? "none" : theme === "dark" ? "1px solid rgb(62, 62, 62)" : "none",
        borderBottom: isLaptop && theme === "dark" ? "1px solid rgb(62, 62, 62)" : "none",
    };

    return (
        <div className={styles.StockShortsCards}>
            {!boxes.length
                ? loading &&
                  Array.from({ length: keys.length || SKELETON_CARD_COUNT }, (_, index) => (
                      <Card className={styles.card} sx={cardSx} key={`skeleton-${index}`}>
                          <div className={styles.wrapper}>
                              <Skeleton className={styles.title} variant="text" width="60%" sx={{ mx: "auto" }} />
                              <Skeleton className={styles.value} variant="text" width="40%" sx={{ mx: "auto", fontSize: "1.5rem" }} />
                              <Skeleton className={styles.chart} variant="rectangular" sx={{ borderRadius: "4px" }} />
                              <Skeleton className={styles.select} variant="text" width="45%" sx={{ mx: "auto" }} />
                          </div>
                      </Card>
                  ))
                : boxes.map(([key, value]) => {
                      const dataKeys = data.map((el) => Object.keys(el)[0]);
                      const keyIndex = dataKeys.indexOf(value.metric);
                      const currentValue = nFormatter(value["previous"], 2);
                      if (currentValue !== "N/A") currentValues.current[key] = currentValue;
                      const isAvailability = (value["shortName"] || value["name"] || "").toLowerCase().includes("availability");

                      return (
                          <Card className={styles.card} sx={cardSx} key={key}>
                              <div className={classNames(styles.wrapper)}>
                                  {isAvailability && !obfuscated ? (
                                      <Tooltip arrow title={AVAILABILITY_INFO}>
                                          <Typography variant="h3" component="h2" className={styles.title} sx={{ cursor: "help" }}>
                                              <span style={{ borderBottom: "1px dotted currentColor" }}>{value["shortName"] || value["name"]}</span>
                                          </Typography>
                                      </Tooltip>
                                  ) : (
                                      <Typography variant="h3" component="h2" className={styles.title}>
                                          {obfuscated ? value["name"] : value["shortName"] || value["name"] || null}
                                      </Typography>
                                  )}
                                  <Typography variant="h2" component="div" className={classNames(styles.value, obfuscatedClassName)}>
                                      {value["prefix"] && `${value["prefix"]} `}
                                      {isAvailability ? formatAvailability(value["current"]) : nFormatter(value["current"], 2)}
                                      {!isAvailability && value["suffix"] && ` ${value["suffix"]}`}
                                  </Typography>
                                  <div className={classNames(styles.chart, obfuscatedClassName)}>
                                      {data[keyIndex]?.[value["metric"]]?.length ? (
                                          <StockShortsDailyInterestChart data={data[keyIndex][value["metric"]]} reflow={is2KorGreater} obfuscated={obfuscated} />
                                      ) : loading ? (
                                          <Skeleton variant="rectangular" width="100%" height="100%" sx={{ borderRadius: "4px" }} />
                                      ) : (
                                          <p>No Chart Available</p>
                                      )}
                                  </div>
                                  <div className={classNames(styles.select, obfuscatedClassName)}>
                                      <Typography variant="h3" component="div" sx={{ fontWeight: 600 }}>
                                          {isAvailability && value["previous"] ? formatAvailability(value["previous"]) : currentValues.current[key]}
                                          <em
                                              style={{
                                                  color: value["pctChange"] === 0 ? null : isPositive(value["pctChange"]) ? positive : negative,
                                              }}
                                          >
                                              {" "}
                                              ({value["pctChange"] === 0 || "N/A" ? null : isPositive(value["pctChange"]) ? "+" : "-"}
                                              {obfuscated ? nFormatter(value["pctChange"], 2) : value["pctChange"]}%)
                                          </em>
                                      </Typography>
                                  </div>
                              </div>
                          </Card>
                      );
                  })}
        </div>
    );
};

export default StockShortsCards;
